/**
 * Example packs: a first run that is not an empty screen.
 *
 * **These are ordinary user data.** A pack installs through the same
 * repositories as anything hand-made, and produces tracks and templates that
 * are indistinguishable from them afterwards — no flag, no reserved id, no
 * protected status. Every one can be renamed, archived and deleted.
 *
 * This module is where use-case leakage would enter the codebase, so the test
 * is explicit: **nothing here may be referenced from the domain model, and the
 * installer must not know what any pack contains.** If a pack's subject matter
 * would look wrong to someone tracking guitar practice or rainfall, that is
 * fine — it is data. If the *model* would look wrong to them, something has
 * gone into the wrong layer.
 */

import type { Result } from '../model/result.js'
import { err, invalid, ok } from '../model/result.js'
import type { CheckInTemplate } from '../model/template.js'
import type { Track, TrackKind } from '../model/track.js'
import type { Storage } from '../ports/storage.js'
import type { Repositories } from './repositories.js'

export interface PackTrack {
  readonly name: string
  readonly unit?: string
  readonly description?: string
  readonly kind: TrackKind
}

export interface PackTemplate {
  readonly name: string
  readonly description?: string
  /** Positions in the pack's own track list — real ids do not exist until install. */
  readonly trackIndices: readonly number[]
}

export interface PackDefinition {
  readonly id: string
  readonly name: string
  readonly description: string
  readonly tracks: readonly PackTrack[]
  readonly templates: readonly PackTemplate[]
}

export interface InstalledPack {
  readonly tracks: readonly Track[]
  readonly templates: readonly CheckInTemplate[]
}

/** Set once the first-run offer has been made, so it is not repeated. */
export const FIRST_RUN_KEY = 'firstRun.packsOffered'

/**
 * Installs a pack through the ordinary repositories.
 *
 * No direct storage writes and no special-casing: whatever validation and
 * numbering a hand-made track goes through, these go through too. Adding a new
 * pack is a data change, never a change here.
 */
export async function installPack(
  pack: PackDefinition,
  repos: Repositories,
): Promise<Result<InstalledPack>> {
  const created: Track[] = []

  for (const definition of pack.tracks) {
    const result = await repos.tracks.create({
      name: definition.name,
      kind: definition.kind,
      ...(definition.unit === undefined ? {} : { unit: definition.unit }),
      ...(definition.description === undefined
        ? {}
        : { description: definition.description }),
    })
    if (!result.ok) {
      return err<InstalledPack>(...result.errors)
    }
    created.push(result.value)
  }

  const templates: CheckInTemplate[] = []
  for (const definition of pack.templates) {
    const trackIds: string[] = []
    for (const index of definition.trackIndices) {
      const track = created[index]
      if (track === undefined) {
        return err<InstalledPack>(
          invalid(
            'pack.template.badIndex',
            `Pack "${pack.id}" references a track it does not define.`,
          ),
        )
      }
      trackIds.push(track.id)
    }

    const result = await repos.templates.create({
      name: definition.name,
      trackIds,
      ...(definition.description === undefined
        ? {}
        : { description: definition.description }),
    })
    if (!result.ok) {
      return err<InstalledPack>(...result.errors)
    }
    templates.push(result.value)
  }

  return ok({ tracks: created, templates })
}

/** Whether the first-run offer has already been made. */
export async function packsOffered(storage: Storage): Promise<boolean> {
  return (await storage.settings.get(FIRST_RUN_KEY)) === 'true'
}

/**
 * Records that the offer was made — including when the user declined.
 *
 * Declining must leave the app completely empty and must not be asked again.
 */
export async function markPacksOffered(storage: Storage): Promise<void> {
  await storage.settings.set(FIRST_RUN_KEY, 'true')
}

// ─────────────────────────────────────────────────────────────────────────────
// Pack definitions. Data only, from here down.
//
// Three packs rather than one opinionated set, chosen to show the range of what
// a track can be — a numeric scale, an ordered word scale, and a yes/no — so
// the shapes are visible before someone designs their own.
// ─────────────────────────────────────────────────────────────────────────────

const ONE_TO_TEN: TrackKind = { kind: 'numeric', min: 1, max: 10, step: 1, decimals: 0 }

const APPETITE_PACK: PackDefinition = {
  id: 'appetite',
  name: 'Appetite and eating',
  description: 'Numeric scales recorded around meals.',
  tracks: [
    { name: 'Hunger', description: 'How hungry, right now.', kind: ONE_TO_TEN },
    { name: 'Fullness', description: 'How full, right now.', kind: ONE_TO_TEN },
    {
      name: 'Food noise',
      description: 'How much food is on your mind.',
      kind: ONE_TO_TEN,
    },
  ],
  templates: [
    { name: 'Before eating', trackIndices: [0, 2] },
    { name: 'After eating', trackIndices: [1, 2] },
  ],
}

const INTENSITY_PACK: PackDefinition = {
  id: 'intensity',
  name: 'Word scales',
  description: 'Ordered word scales, for anything easier to name than to number.',
  tracks: [
    {
      name: 'Urge',
      description: 'An ordered scale — charts and averages like a number.',
      kind: {
        kind: 'enum',
        ordered: true,
        options: [
          { id: 'none', label: 'None', score: 0 },
          { id: 'mild', label: 'Mild', score: 1 },
          { id: 'strong', label: 'Strong', score: 2 },
          { id: 'overwhelming', label: 'Overwhelming', score: 3 },
        ],
      },
    },
    {
      name: 'Setting',
      description: 'An unordered list — counted and grouped, never averaged.',
      kind: {
        kind: 'enum',
        ordered: false,
        options: [
          { id: 'home', label: 'Home', score: null },
          { id: 'work', label: 'Work', score: null },
          { id: 'out', label: 'Out', score: null },
        ],
      },
    },
  ],
  templates: [{ name: 'Moment', trackIndices: [0, 1] }],
}

const HABIT_PACK: PackDefinition = {
  id: 'habit',
  name: 'Yes or no',
  description: 'Did it happen? The simplest thing to track.',
  tracks: [
    {
      name: 'Did it',
      description: 'A plain yes or no, counted as a rate over time.',
      kind: { kind: 'boolean', trueLabel: 'Yes', falseLabel: 'No' },
    },
  ],
  templates: [{ name: 'Daily check', trackIndices: [0] }],
}

export const examplePacks: readonly PackDefinition[] = [
  APPETITE_PACK,
  INTENSITY_PACK,
  HABIT_PACK,
]
